import { useEffect } from "react";
import { CANVAS_TOOLS } from "../../../features/canvas/nativeCanvasElements";
import { NUDGE_STEP } from "../../../features/canvas/canvasDefaults";
import { copyCanvasSelection } from "./canvasClipboard";
import { TOOL_SHORTCUTS } from "./canvasWorkspace.constants";

const DELETE_KEYS = Object.freeze(["Backspace", "Delete"]);
const IMAGE_TOOL_KEYS = Object.freeze(["9"]);
const INPUT_SELECTOR = "input, textarea, select, [contenteditable='true']";
const NUDGE_DELTAS = Object.freeze({
  ArrowDown: { x: 0, y: 1 },
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
});

export const isEditableTarget = (target) =>
  Boolean(target?.closest?.(INPUT_SELECTOR));
const hasCommandModifier = (event) => event.ctrlKey || event.metaKey;

const handleCopyShortcut = ({ engine, event }) => {
  if (copyCanvasSelection(engine)) event.preventDefault();
};

const handleCutShortcut = ({ engine, event }) => {
  if (!copyCanvasSelection(engine)) return;
  event.preventDefault();
  engine.deleteSelected();
};

// Cmd/Ctrl+V is intentionally absent: preventing default on the keydown
// would suppress the native `paste` event that useCanvasPaste relies on.
const COMMAND_SHORTCUT_HANDLERS = Object.freeze({
  a: ({ engine, event }) => {
    event.preventDefault();
    engine.selectAll();
  },
  c: handleCopyShortcut,
  d: ({ engine, event }) => {
    event.preventDefault();
    engine.duplicateSelected();
  },
  s: ({ event, onSaveShortcut }) => {
    event.preventDefault();
    onSaveShortcut?.();
  },
  x: handleCutShortcut,
  y: ({ engine, event }) => {
    event.preventDefault();
    engine.redo();
  },
  z: ({ engine, event }) => {
    event.preventDefault();
    if (event.shiftKey) engine.redo();
    else engine.undo();
  },
});

const handleCommandShortcut = ({ engine, event, onSaveShortcut }) => {
  COMMAND_SHORTCUT_HANDLERS[event.key.toLowerCase()]?.({
    engine,
    event,
    onSaveShortcut,
  });
};

const handleNudge = ({ engine, event }) => {
  const direction = NUDGE_DELTAS[event.key];
  const step = event.shiftKey ? NUDGE_STEP.LARGE : NUDGE_STEP.SMALL;
  event.preventDefault();
  engine.nudgeSelected({ x: direction.x * step, y: direction.y * step });
};

const handleEscape = ({ engine, onEscapeIdle }) => {
  if (engine.cancelInteraction()) return;
  onEscapeIdle?.();
};

const handlePlainKey = ({ engine, event, onEscapeIdle, onImageToolRequest }) => {
  if (DELETE_KEYS.includes(event.key)) return engine.deleteSelected();
  if (event.key === "Escape") return handleEscape({ engine, onEscapeIdle });
  if (event.key === " ") {
    event.preventDefault();
    return engine.setSpacePanning(true);
  }
  if (NUDGE_DELTAS[event.key]) return handleNudge({ engine, event });
  if (IMAGE_TOOL_KEYS.includes(event.key)) return onImageToolRequest();

  const tool = TOOL_SHORTCUTS[event.key.toLowerCase()];
  if (tool) engine.setTool(tool);
};

/**
 * Global keyboard shortcuts for the canvas: tool switching, undo/redo,
 * delete, duplicate, select-all, save, arrow-key nudging and space-to-pan.
 * Escape is layered: it cancels the active interaction first, and only when
 * the canvas is already idle does it fall through to `onEscapeIdle`.
 * @param {{ engine: object, onEscapeIdle?: Function, onImageToolRequest: Function, onSaveShortcut?: Function }} params
 * @returns {void}
 */
export const useCanvasKeyboard = ({
  engine,
  onEscapeIdle,
  onImageToolRequest,
  onSaveShortcut,
}) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isEditableTarget(event.target)) return;
      if (hasCommandModifier(event)) {
        return handleCommandShortcut({ engine, event, onSaveShortcut });
      }
      if (event.altKey) return;
      handlePlainKey({ engine, event, onEscapeIdle, onImageToolRequest });
    };
    const handleKeyUp = (event) => {
      if (event.key === " ") engine.setSpacePanning(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [engine, onEscapeIdle, onImageToolRequest, onSaveShortcut]);
};

export const isSelectionTool = (tool) => tool === CANVAS_TOOLS.SELECT;
